"use client";

import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";

import { handleSilentError } from "@/shared/api/error-handler";
import { logger } from "@/shared/lib/logger";
import { useAuthStore } from "@/shared/model/auth-store";
import {
  NotificationConnection,
  UserNotification,
  type FriendActivityAddedPayload,
  type MatchAutoLobbyTriggerPayload,
} from "@/shared/lib/signalr/notification-connection";

type NotificationCallback = (notification: UserNotification) => void;
type FriendActivityCallback = (payload: FriendActivityAddedPayload) => void;

const NotificationConnectionContext = createContext<{
  subscribe: (cb: NotificationCallback) => () => void;
  subscribeFriendActivity: (cb: FriendActivityCallback) => () => void;
} | null>(null);

export function NotificationConnectionProvider({
  children,
}: {
  children?: React.ReactNode;
}) {
  const tUi = useTranslations("Ui");
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isInitialized = useAuthStore((s) => s.isInitialized);

  // Счётчик колокольчика живёт в React Query, а не в Zustand-сторе, поэтому
  // одного markAsReadLocal мало: без инвалидации бейдж продолжает показывать
  // старое число, пока не истечёт staleTime (KAN-916).
  const qc = useQueryClient();
  const invalidateNotificationQueries = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["notifications"] }).catch(() => {});
  }, [qc]);

  const connectionRef = useRef<NotificationConnection | null>(null);
  const subscribersRef = useRef<Set<NotificationCallback>>(new Set());
  const subscribersFriendActivityRef = useRef<Set<FriendActivityCallback>>(new Set());

  const ctxValue = useRef({
    subscribe: (cb: NotificationCallback) => {
      subscribersRef.current.add(cb);
      return () => {
        subscribersRef.current.delete(cb);
      };
    },
    subscribeFriendActivity: (cb: FriendActivityCallback) => {
      subscribersFriendActivityRef.current.add(cb);
      return () => {
        subscribersFriendActivityRef.current.delete(cb);
      };
    },
  }).current;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isInitialized || isLoading || !isAuthenticated) {
      if (connectionRef.current) {
        connectionRef.current.offReceiveNotification();
        connectionRef.current.offFriendActivityAdded();
        connectionRef.current.offNotificationRead();
        connectionRef.current.offAllNotificationsRead();
        connectionRef.current.offNotificationDeleted();
        connectionRef.current.offAllNotificationsCleared();
        connectionRef.current.stop();
        connectionRef.current = null;
      }
      return;
    }

    const conn = new NotificationConnection();
    connectionRef.current = conn;

    conn.onReceiveNotification((notification: UserNotification) => {
      // KAN-919: подтверждаем приём. Пока подтверждения нет, уведомление на сервере
      // числится недоставленным и будет дослано при следующем подключении — так
      // пропущенное перестаёт теряться, а «доставлено» перестаёт быть догадкой сервера.
      conn.acknowledgeNotification(notification.id);

      subscribersRef.current.forEach((cb) => {
        try {
          cb(notification);
        } catch (err) {
          handleSilentError(err, {
            component: "notification-connection",
            action: "notify.subscriber",
          });
        }
      });
    });

    conn.onFriendActivityAdded((payload: FriendActivityAddedPayload) => {
      subscribersFriendActivityRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (err) {
          handleSilentError(err, {
            component: "notification-connection",
            action: "notify.friendActivitySubscriber",
          });
        }
      });
    });

    // Cross-device read-sync: события от backend, когда юзер взаимодействует
    // с уведомлениями на одном из своих устройств. Список живёт в React Query —
    // инвалидируем его ключи (KAN-1179: Zustand-дубль удалён).
    conn.onNotificationRead(() => {
      try {
        invalidateNotificationQueries();
      } catch (err) {
        handleSilentError(err, {
          component: "notification-connection",
          action: "invalidate.markAsRead",
        });
      }
    });

    conn.onAllNotificationsRead(() => {
      try {
        invalidateNotificationQueries();
      } catch (err) {
        handleSilentError(err, {
          component: "notification-connection",
          action: "invalidate.markAllAsRead",
        });
      }
    });

    conn.onNotificationDeleted(() => {
      try {
        invalidateNotificationQueries();
      } catch (err) {
        handleSilentError(err, {
          component: "notification-connection",
          action: "invalidate.deleteNotification",
        });
      }
    });

    conn.onAllNotificationsCleared(() => {
      try {
        invalidateNotificationQueries();
      } catch (err) {
        handleSilentError(err, {
          component: "notification-connection",
          action: "invalidate.clearAllNotifications",
        });
      }
    });

    // Авто-триггер создания лобби. Backend шлёт хосту за 5 мин до старта
    // (или сразу при admin override). Payload включает tournamentType + bestOf,
    // чтобы фронт активировал правильный watcher (set_active_match vs 1v1)
    // ДО создания лобби — иначе результат игры никуда не отправится.
    conn.onMatchAutoLobbyTrigger(async (payload: MatchAutoLobbyTriggerPayload) => {
      try {
        const { checkIsTauri, invokeTauri } = await import("@/shared/lib/tauri");
        if (!checkIsTauri()) return; // на сайте — обычное уведомление уже придёт

        const tokenResp = await fetch(`${window.location.origin}/api/me/desktop-token`, {
          credentials: "include",
        });
        if (!tokenResp.ok) {
          console.warn("[AutoLobbyTrigger] desktop-token fetch failed", tokenResp.status);
          return;
        }
        const { token } = (await tokenResp.json()) as { token: string };

        const tournamentType: string = payload.tournamentType ?? "Classic";
        const tournamentId: string = payload.tournamentId ?? "";
        const bestOf: number = typeof payload.bestOf === "number" ? payload.bestOf : 1;

        const activeReq = {
          matchId: payload.matchId,
          tournamentId,
          authToken: token,
          backendUrl: window.location.origin,
          bestOf,
        };
        // КРИТИЧНО: активируем watcher ПЕРЕД созданием лобби, чтобы он успел
        // зацепить ChampSelect → InProgress → EndOfGame phase transitions.
        if (tournamentType === "SoloOneVsOne") {
          await invokeTauri("set_active_1v1_match", { req: activeReq });
        } else {
          await invokeTauri("set_active_match", { req: activeReq });
        }
        logger.debug(tUi("litNotificationConnection.autolobbytriggerAktivnyyMatchZaregistrir"), {
          matchId: payload.matchId,
          tournamentType,
          bestOf,
        });

        // Помечаем лобби созданным в Rust-state ДО вызова create_tournament_lobby.
        // Это предотвращает дублирование: если active_match_poller успеет сначала
        // (при совпадении по времени), он увидит флаг и пропустит создание.
        await invokeTauri("mark_lobby_created", { matchId: payload.matchId });

        await invokeTauri("create_tournament_lobby", {
          req: {
            matchId: payload.matchId,
            authToken: token,
            backendUrl: window.location.origin,
          },
        });
      } catch (err) {
        handleSilentError(err, {
          component: "notification-connection",
          action: "autoLobbyTrigger",
        });
      }
    });

    // KAN-919: разрыв соединения оставлял ленту и бейдж на состоянии момента обрыва —
    // `onreconnected` не был зарегистрирован, а refetch по фокусу у этих запросов выключен.
    // Пропущенное за время разрыва бэкенд дошлёт сам, но состояние, изменившееся молча
    // (прочитано на другом устройстве, счётчик), приезжает только перезапросом.
    conn.onReconnected(() => {
      invalidateNotificationQueries();
    });

    // Возврат на вкладку — вторая разумная точка обновления: вкладка, пролежавшая ночь,
    // показывает вчерашнюю ленту. Слушатель точечный; включать глобальный
    // refetchOnWindowFocus нельзя — он заденет весь проект.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        invalidateNotificationQueries();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // bfcache (KAN-1173): открытый WebSocket не даёт сохранить страницу в кэш
    // «назад/вперёд». Закрываем на pagehide; при возврате из кэша поднимаем и
    // перезапрашиваем ленту — пока страница была заморожена, события не приходили.
    const onPageHide = () => {
      void conn.stop();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      conn.start().catch((err) =>
        handleSilentError(err, {
          component: "notification-connection",
          action: "connection.restoreFromBfcache",
        }),
      );
      invalidateNotificationQueries();
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    conn.start().catch((err) =>
      // Хаб переподключится сам — пользователю не сообщаем, но сбой виден в GlitchTip
      handleSilentError(err, {
        component: "notification-connection",
        action: "connection.start",
      }),
    );

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      conn.offReceiveNotification();
      conn.offFriendActivityAdded();
      conn.offNotificationRead();
      conn.offAllNotificationsRead();
      conn.offNotificationDeleted();
      conn.offAllNotificationsCleared();
      conn.offMatchAutoLobbyTrigger();
      conn.stop();
      connectionRef.current = null;
    };
  }, [isAuthenticated, isInitialized, isLoading, invalidateNotificationQueries, tUi]);

  return (
    <NotificationConnectionContext.Provider value={ctxValue}>
      {children ?? null}
    </NotificationConnectionContext.Provider>
  );
}

/**
 * Subscribe to SignalR notifications. Callback is auto-registered/unregistered on mount/unmount.
 */
export function useOnNotification(callback: NotificationCallback) {
  const ctx = useContext(NotificationConnectionContext);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!ctx) return;
    const handler: NotificationCallback = (n) => callbackRef.current(n);
    return ctx.subscribe(handler);
  }, [ctx]);
}

/**
 * Subscribe to FriendActivityAdded SignalR event. Callback is auto-unregistered on unmount.
 */
export function useOnFriendActivity(callback: FriendActivityCallback) {
  const ctx = useContext(NotificationConnectionContext);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!ctx) return;
    const handler: FriendActivityCallback = (p) => callbackRef.current(p);
    return ctx.subscribeFriendActivity(handler);
  }, [ctx]);
}
